import type { User } from '@/types'

/**
 * Get the role name from a user object
 * Checks multiple possible locations where role name might be stored
 */
export function getUserRoleName(user: User | null): string | null {
  if (!user) return null
  
  // Check role_name or user_role_name from API (preferred)
  const roleName = (user as any)?.role_name || (user as any)?.user_role_name
  if (roleName) return roleName
  
  // Check user_role object/string
  const userRole = (user as any)?.user_role
  if (userRole) {
    if (typeof userRole === 'string') {
      try {
        const parsed = JSON.parse(userRole)
        return parsed?.name || null
      } catch {
        return null
      }
    } else if (typeof userRole === 'object' && userRole?.name) {
      return userRole.name
    }
  }
  
  // Fallback to role field
  return (user as any)?.role || null
}

/**
 * Check if user is a Senior Team Lead or Team Lead
 */
export function isTeamLead(user: User | null): boolean {
  const roleName = getUserRoleName(user)
  return roleName === 'Senior Team Lead' || roleName === 'Team Lead'
}

/** Roles allowed to search/reassign the parent-ticket agent on create/edit. */
export const PARENT_TICKET_REASSIGN_ROLES = [
  'Senior Team Lead',
  'Team Lead',
  'Web Admin',
  'Developer Account',
] as const

/**
 * Whether the user may change the parent ticket agent (TL / STL / Web Admin / Developer Account).
 */
export function canReassignParentTicketAgent(user: User | null): boolean {
  const roleName = getUserRoleName(user)
  return (
    roleName != null &&
    (PARENT_TICKET_REASSIGN_ROLES as readonly string[]).includes(roleName)
  )
}

/**
 * Whether the user may assign/reassign agents on Incoming Request and Tickets list UIs.
 * Same role set as parent-ticket form agent reassignment.
 */
export function canAssignSupportAgent(user: User | null): boolean {
  return canReassignParentTicketAgent(user)
}

/** Reporting-tree members (TL/STL). */
export const ASSIGN_AGENTS_MEMBERS_PATH = '/user-management/users/members'

/** Org-wide assignable agents (Web Admin / Developer Account — no reports_to). */
export const ASSIGN_AGENTS_GLOBAL_PATH = '/user-management/users/assignable-agents'

/**
 * Agent list API for Agent Status.
 * TL/STL use reports_to members; Web Admin / Developer Account use global assignable agents.
 */
export function getAssignAgentListPath(user: User | null): string {
  return isTeamLead(user) ? ASSIGN_AGENTS_MEMBERS_PATH : ASSIGN_AGENTS_GLOBAL_PATH
}

/**
 * Agent list API for Incoming Request and Tickets assign menus.
 * Team Lead is limited to the reporting tree. Senior Team Lead, Web Admin,
 * and Developer Account use the org-wide Team Lead and Service Desk pool.
 */
export function getTicketAssignAgentListPath(user: User | null): string {
  return isRegularTeamLead(user) ? ASSIGN_AGENTS_MEMBERS_PATH : ASSIGN_AGENTS_GLOBAL_PATH
}

/**
 * Check if user is a Senior Team Lead
 */
export function isSeniorTeamLead(user: User | null): boolean {
  const roleName = getUserRoleName(user)
  return roleName === 'Senior Team Lead'
}

/**
 * Check if user is a Team Lead (but not Senior Team Lead)
 */
export function isRegularTeamLead(user: User | null): boolean {
  const roleName = getUserRoleName(user)
  return roleName === 'Team Lead'
}
